import React, { useState, useEffect } from "react";
import LoadingWheel from "./LoadingWheel";

const WEB_DISPLAY_URLS = ["/", "/collections"];

const OptimisedImage = ({
  src,
  fallbackSrc,
  webpFallbackSrc,
  className,
  style,
  alt,
  id,
  width,
  height,
  isLCP = false,
  loadingBehavior,
}) => {
  const baseSrc = src
    .replace(/\.[^/.]+$/, "")
    .replace("/img/", "/img_optimised/");

  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkImage = async () => {
      setLoading(true);
      if (await imageExists(`${baseSrc}.webp`)) {
        setImageUrl(`${baseSrc}.webp`);
      } else if (webpFallbackSrc && await imageExists(webpFallbackSrc)) {
        setImageUrl(webpFallbackSrc);
      } else {
        setImageUrl(src);
      }
      // Always clear the loader once the URL is determined, so it never gets
      // stuck if the <img> onLoad event doesn't fire (e.g. cached images,
      // slow networks, or browser quirks).
      setLoading(false);
    };

    checkImage();
  }, [src, webpFallbackSrc]);

  const handleImageLoad = () => setLoading(false);

  const handleImageError = () => {
    if (fallbackSrc && imageUrl !== fallbackSrc) {
      setImageUrl(fallbackSrc);
    }
  };

  const imageExists = (url) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      // Resolve false after 8 s so a slow/unresponsive server never leaves
      // the loader spinning indefinitely.
      const timer = setTimeout(() => resolve(false), 8000);
      img.onload = () => { clearTimeout(timer); resolve(true); };
      img.onerror = () => { clearTimeout(timer); resolve(false); };
      img.src = url;
    });
  };

  return (
    <>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <LoadingWheel />
        </div>
      )}

      <img
        src={imageUrl}
        loading={loadingBehavior ?? (isLCP ? "eager" : "lazy")}
        fetchpriority={isLCP ? "high" : undefined}
        decoding="async"
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={loading ? { visibility: "hidden", ...style } : style}
        {...(id && { id })}
        {...(className && { className })}
        {...(alt && { alt })}
        {...(width && { width })}
        {...(height && { height })}
      />
    </>
  );
};

export default OptimisedImage;
